import { forwardRef, memo } from 'react';
import { SqlMonaco } from '../../common/components/SqlMonaco';
import type { SqlMonacoHandle, IntelliSenseStatusType, WordWrapSetting, SqlMonacoProps } from '../../common/utils/sqlMonacoTypes';
import type { DatabaseEngineId } from '@/core/engines';

interface QueryEditorProps {
  initialQuery?: string;
  onChange?: (query: string) => void;
  onSelectionChange?: SqlMonacoProps['onSelectionChange'];
  /** Engine ID for dialect-aware features (e.g., formatting) */
  engineId?: DatabaseEngineId;
  /** Connection ID for IntelliSense context */
  connectionId?: string;
  /** Database name for IntelliSense context */
  database?: string;
  className?: string;
  /** Word wrap setting for this editor */
  wordWrap?: WordWrapSetting;
  /** Callback when IntelliSense status changes */
  onIntelliSenseStatusChange?: (status: IntelliSenseStatusType, message?: string) => void;
}

/** Handle exposed by QueryEditor for external access to editor methods */
export type QueryEditorHandle = SqlMonacoHandle;

const noopOnChange = (_value: string): void => { void _value; };

// Memoised so progress-tick re-renders in QueryEditorTab (every 5k-row batch
// during large-result streaming) don't churn the Monaco subtree and make
// typing feel laggy. Callers must pass stable callback refs for this to help.
export const QueryEditor = memo(forwardRef<QueryEditorHandle, QueryEditorProps>(function QueryEditor({
  initialQuery = '',
  onChange,
  onSelectionChange,
  engineId,
  connectionId,
  database,
  className = '',
  wordWrap,
  onIntelliSenseStatusChange,
}, ref) {
  if (typeof window !== 'undefined' && (window as unknown as { __SQL_STUDIO_PROFILE__?: boolean }).__SQL_STUDIO_PROFILE__) {
     
    console.log('[PROFILE] QueryEditor render');
  }
  return (
    <div className={`w-full h-full flex flex-col relative ${className}`}>
      <SqlMonaco
        ref={ref}
        value={initialQuery}
        onChange={onChange ?? noopOnChange}
        onSelectionChange={onSelectionChange}
        engineId={engineId}
        connectionId={connectionId}
        database={database}
        wordWrap={wordWrap}
        onIntelliSenseStatusChange={onIntelliSenseStatusChange}
      />
    </div>
  );
}));
